import type { CSSProperties } from 'react';
import { useState } from 'react';

import type { RegionNode } from '../../../../api/regions';

import { useCreateRegion } from './regionsQueries';

interface Props {
  regions: RegionNode[];
  onClose: () => void;
  /** Called with the new region's id after a successful create. */
  onCreated: (id: string) => void;
  /** Preselect a parent (e.g. "Add subregion" from a region's detail page). */
  initialParentId?: string;
}

interface FlatOption {
  id: string;
  label: string;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  background: 'rgba(15,25,18,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const panelStyle: CSSProperties = {
  width: 380,
  maxWidth: '90vw',
  background: 'var(--nb-bg)',
  border: '1px solid var(--nb-border)',
  borderRadius: 12,
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const fieldStyle: CSSProperties = {
  height: 34,
  padding: '0 11px',
  borderRadius: 9,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 13,
  color: 'var(--nb-ink)',
  boxSizing: 'border-box',
};

const labelStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

/** Flatten the tree to indented <option>s so a parent can be picked by name. */
function flattenOptions(regions: RegionNode[]): FlatOption[] {
  const out: FlatOption[] = [];
  const walk = (node: RegionNode, depth: number) => {
    out.push({ id: node.id, label: `${'  '.repeat(depth)}${node.name}` });
    node.children.forEach((c) => walk(c, depth + 1));
  };
  regions.forEach((r) => walk(r, 0));
  return out;
}

/**
 * Minimal create flow for the Region Management landing — name + optional
 * parent. A null parent makes a root region; a parent makes a subregion.
 */
export function RegionCreateModal({ regions, onClose, onCreated, initialParentId }: Props) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string>(initialParentId ?? '');
  const create = useCreateRegion();
  const options = flattenOptions(regions);
  const trimmed = name.trim();

  const submit = () => {
    if (!trimmed || create.isPending) return;
    create.mutate(
      { name: trimmed, parentId: parentId || null },
      { onSuccess: (node) => onCreated(node.id) },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New region"
      onClick={onClose}
      style={overlayStyle}
    >
      <div onClick={(e) => e.stopPropagation()} style={panelStyle}>
        <div className="nb-display" style={{ fontSize: 17, color: 'var(--nb-bark-dark)' }}>
          New region
        </div>

        <label style={labelStyle}>
          <span className="nb-eyebrow" style={{ fontSize: 9 }}>
            Name
          </span>
          <input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="e.g. Pacific Northwest"
            style={fieldStyle}
          />
        </label>

        <label style={labelStyle}>
          <span className="nb-eyebrow" style={{ fontSize: 9 }}>
            Parent (optional)
          </span>
          <select value={parentId} onChange={(e) => setParentId(e.target.value)} style={fieldStyle}>
            <option value="">Top-level region</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        {create.isError && (
          <div style={{ fontSize: 12, color: 'var(--nb-danger, #dc2626)' }}>
            {(create.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
          <button type="button" className="nb-btn" style={{ fontSize: 12 }} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="nb-btn nb-btn-primary"
            style={{ fontSize: 12 }}
            disabled={!trimmed || create.isPending}
            onClick={submit}
          >
            {create.isPending ? 'Creating…' : 'Create region'}
          </button>
        </div>
      </div>
    </div>
  );
}
